import { RematchDispatch, RematchRootState } from "@rematch/core";
import { isPlainObject, omit } from "lodash";

import {
  CustomerGroupListItem,
  CustomerGroupListItemFromDb,
} from "../../lib/models/CustomerGroupListItem";
import { initialState as deactivatedCustomerGroupFilter } from "../../models/customerGroupFilter";
import {
  CustomerGroupFilterState,
  SupplyType,
  Tariff,
  TariffTypesPayload,
} from "../../types/index";
import { RootModel } from "..";

export const checkToken = (
  rootState: RematchRootState<RootModel>,
  dispatchFail: () => void
): string | null => {
  const token = rootState.auth.token;

  if (!token) {
    // eslint-disable-next-line no-console
    console.error("no token available!");
    dispatchFail();
    return null;
  }
  return token;
};

export const signoutIfUnauthorized = (
  error: any,
  dispatch: RematchDispatch<RootModel>
): void => {
  if (error === 401) {
    localStorage.removeItem("login");
    dispatch.auth.signout();
  }
};

/* Adds missing filter settings to filter settings of saved customer group (using deactivatedCustomerGroupFilter).
This is also done for nested filters like electricityFilter, gasFilter, heatingFilter or filterGroupsEnabled.
Further adaptions of the filter settings have to be made manually. */
const autoAddNonExistingFilters = (
  savedFilter: Record<string, unknown>,
  templateFilter: CustomerGroupFilterState | Record<string, unknown>
): Record<string, unknown> => {
  const savedFilterKeys = Object.keys(savedFilter);
  const nestedObjectsKeys: string[] = [];

  savedFilterKeys.forEach((key) => {
    if (isPlainObject(savedFilter[key]) && isPlainObject(templateFilter[key])) {
      nestedObjectsKeys.push(key);
    }
  });

  const adaptedNestedFilters = {};
  nestedObjectsKeys.forEach((key) => {
    adaptedNestedFilters[key] = autoAddNonExistingFilters(
      savedFilter[key] as Record<string, unknown>,
      templateFilter[key] as Record<string, unknown>
    );
  });
  return {
    ...templateFilter,
    ...savedFilter,
    ...adaptedNestedFilters,
  };
};

const adaptFilterGroupsEnabled = (
  autoExtendedFilterSettings: Record<string, unknown>
) => {
  const filterGroupsEnabled = (autoExtendedFilterSettings as Record<
    string,
    unknown
  > & {
    filterGroupsEnabled: Record<string, unknown>;
  }).filterGroupsEnabled;
  const customerInformationPredecessors = [
    "customerProperties",
    "customerContacts",
    "customerStatus",
  ];
  let oneOfCustomerInformationPredecessorsEnabled = false;
  Object.keys(filterGroupsEnabled).forEach((filterGroupName) => {
    if (customerInformationPredecessors.includes(filterGroupName)) {
      if (filterGroupsEnabled[filterGroupName]) {
        oneOfCustomerInformationPredecessorsEnabled = true;
      }
    }
  });
  const filterGroupsEnabledAdapted = {
    ...omit(filterGroupsEnabled, customerInformationPredecessors),
    customerInformation: oneOfCustomerInformationPredecessorsEnabled,
  };
  return filterGroupsEnabledAdapted;
};

export const adaptSavedFilterSettings = (
  customerGroupListItemsFromDb: CustomerGroupListItemFromDb[]
): CustomerGroupListItem[] => {
  const adaptedCustomerGroupListItems: CustomerGroupListItem[] = [];
  customerGroupListItemsFromDb.forEach((group) => {
    const { filterSettings } = group;

    try {
      const autoExtendedFilterSettings = autoAddNonExistingFilters(
        filterSettings,
        deactivatedCustomerGroupFilter
      );
      const adaptedFilterSettings = {
        filterGroupsEnabled: adaptFilterGroupsEnabled(
          autoExtendedFilterSettings
        ),
      };
      adaptedCustomerGroupListItems.push({
        ...group,
        filterSettings: {
          ...autoExtendedFilterSettings,
          ...adaptedFilterSettings,
        } as CustomerGroupFilterState,
        adaptionError: false,
      });
    } catch {
      adaptedCustomerGroupListItems.push({
        // simple but dirty solution to please typescript, should be improved.
        ...group,
        filterSettings: <CustomerGroupFilterState>(
          (<unknown>group.filterSettings)
        ),
        adaptionError: true,
      });
    }
  });
  return adaptedCustomerGroupListItems;
};

export const mapSupplyTypes = (tariffs: TariffTypesPayload): Tariff[] => {
  return tariffs.map((tariff) => {
    if (tariff.supplyType === "Grundversorgung") {
      return { ...tariff, supplyType: SupplyType.GV };
    } else if (tariff.supplyType === "Sondervertrag") {
      return { ...tariff, supplyType: SupplyType.SV };
    } else {
      return { ...tariff, supplyType: null };
    }
  });
};

export * from "./multiRequestHandlers";



// WEBPACK FOOTER //
// ./src/models/utilities/index.ts